import React, { useState } from 'react';
import { Download, CheckCircle, AlertCircle, X } from 'lucide-react';
import { useDownloadProgress } from '../contexts/DownloadProgressContext';
import { DownloadProgressModal } from './DownloadProgressModal';

export const DownloadProgressIcon: React.FC = () => {
  const { hasActiveDownloads, activeDownloads } = useDownloadProgress();
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!hasActiveDownloads) {
    return null;
  }

  const completedCount = activeDownloads.filter(d => d.status === 'completed').length;
  const failedCount = activeDownloads.filter(d => d.status === 'failed').length;
  const inProgressCount = activeDownloads.filter(d => d.status === 'in_progress').length;

  const getIcon = () => {
    if (failedCount > 0) {
      return <AlertCircle className="h-5 w-5 text-white" />;
    }
    if (completedCount > 0 && inProgressCount === 0) {
      return <CheckCircle className="h-5 w-5 text-white" />;
    }
    if (inProgressCount > 0) {
      return <Download className="h-5 w-5 text-white animate-pulse" />;
    }
    return <Download className="h-5 w-5 text-blue-500" />;
  };

  const getTooltipText = () => {
    if (failedCount > 0) {
      return `${failedCount} download(s) failed`;
    }
    if (completedCount > 0 && inProgressCount === 0) {
      return `${completedCount} download(s) completed`;
    }
    if (inProgressCount > 0) {
      return `${inProgressCount} download(s) in progress`;
    }
    return 'Download progress';
  };

  return (
    <>
      {/* Overlay to ensure buttons stay clickable */}
      <div 
        className="fixed inset-0 pointer-events-none z-[9998]" 
        style={{ 
          zIndex: 9998,
          pointerEvents: 'none'
        }}
      />
      
      <div className="fixed bottom-6 right-6 z-[9999]" style={{ zIndex: 9999 }}>
        <div
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsModalOpen(true);
          }}
          className={`group relative rounded-full shadow-lg border p-3 hover:shadow-xl transition-all duration-200 hover:scale-105 cursor-pointer ${
            inProgressCount > 0 
              ? 'bg-blue-500 text-white animate-pulse' 
              : failedCount > 0 
                ? 'bg-red-500 text-white' 
                : completedCount > 0 
                  ? 'bg-green-500 text-white' 
                  : 'bg-white border-gray-200'
          }`}
          style={{ 
            zIndex: 9999,
            position: 'relative',
            pointerEvents: 'auto'
          }}
        >
          {getIcon()}
          
          {/* Progress indicator ring */}
          {inProgressCount > 0 && (
            <div className="absolute -inset-1 rounded-full">
              <svg className="w-8 h-8 transform -rotate-90" viewBox="0 0 36 36">
                <path
                  className="text-white/30"
                  stroke="currentColor"
                  strokeWidth="3"
                  fill="none"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                <path
                  className="text-white animate-pulse"
                  stroke="currentColor"
                  strokeWidth="3"
                  fill="none"
                  strokeDasharray="100, 100"
                  strokeDashoffset="25"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
              </svg>
            </div>
          )}

          {/* Badge with count */}
          <div className={`absolute -top-1 -right-1 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center font-bold border-2 border-white ${
            inProgressCount > 0 
              ? 'bg-blue-600 animate-pulse' 
              : failedCount > 0 
                ? 'bg-red-600' 
                : 'bg-green-600'
          }`}>
            {activeDownloads.length}
          </div>
        </div>
      </div>

      <DownloadProgressModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </>
  );
};